import React from "react";
import type { BookingDraft } from "./bookingDraft";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";

type Actions = {
  setSpecialist(id: string | null): void;
  toggleService(id: string): void;
  setServices(ids: string[]): void;
  setDateISO(d: string | null): void;
  setStartISO(iso: string | null): void;
  setLastHour(offer: BookingDraft["lastHour"]): void;
  setClaimToken(token: string | null): void;
  // NEW
  setClientName(v: string): void;
  setClientPhone(v: string): void;
  setClientEmail(v: string): void;
  setClientComment(v: string): void;
  setConfirmMethod(m: 1 | 2): void;
  setMessengerChannel(ch: string): void;
  /** the review step's one-time default: the method and channel, marked as preset */
  presetConfirm(m: 1 | 2, ch?: string): void;
  setSmsConsent(on: boolean): void;
  reset(): void;
};

const makeInitial = (companyId?: number): BookingDraft => ({
  companyId,
  specialistId: null,
  serviceIds: [],
  dateISO: null,
  startISO: null,
  lastHour: null,
  claimToken: null,
  contact: { name: "", phone: "", email: "", comment: "" },
  confirmMethod: 1,
  messengerChannel: "telegram",
  smsConsent: false,
});

function reducer(state: BookingDraft, action: any): BookingDraft {
  switch (action.type) {
    case "setSpecialist":
      return { ...state, specialistId: action.id };
    case "toggleService":
      return state.serviceIds.includes(action.id)
        ? { ...state, serviceIds: state.serviceIds.filter((x) => x !== action.id) }
        : { ...state, serviceIds: [...state.serviceIds, action.id] };
    case "setServices":
      return { ...state, serviceIds: action.ids };
    case "setDateISO":
      return { ...state, dateISO: action.d };
    case "setStartISO":
      return { ...state, startISO: action.iso };
    case "setLastHour":
      return { ...state, lastHour: action.offer ?? null };
    case "setClaimToken":
      return { ...state, claimToken: action.token ?? null };

    // NEW contact reducers
    case "setClientName":
      return { ...state, contact: { ...state.contact, name: action.v } };
    case "setClientPhone":
      return { ...state, contact: { ...state.contact, phone: action.v } };
    case "setClientEmail":
      return { ...state, contact: { ...state.contact, email: action.v } };
    case "setClientComment":
      return { ...state, contact: { ...state.contact, comment: action.v } };
    case "setConfirmMethod":
      return { ...state, confirmMethod: action.m };
    case "setMessengerChannel":
      return { ...state, messengerChannel: action.ch };
    case "presetConfirm":
      return { ...state, confirmMethod: action.m, messengerChannel: action.ch ?? state.messengerChannel, confirmPreset: true };
    case "setSmsConsent":
      return { ...state, smsConsent: Boolean(action.on) };

    case "reset":
      return makeInitial(state.companyId);
    default:
      return state;
  }
}

const Ctx = React.createContext<{ draft: BookingDraft; actions: Actions } | null>(null);

export function BookingDraftProvider({ children, companyId }: { children: React.ReactNode; companyId?: number }) {
  const [draft, dispatch] = React.useReducer(reducer, makeInitial(companyId));
  const actions: Actions = {
    setSpecialist: (id) => {
      console.log("[BookingDraft] action: setSpecialist", id);
      // Optional trimming: remove services the selected master doesn't provide
      if (id && id !== "any") {
        try {
          const allowed = new Set(getServiceRegistry().getServiceIdsByMaster(Number(id)).map(String));
          const trimmed = (draft.serviceIds ?? []).filter((sid) => allowed.has(sid));
          if (trimmed.length !== (draft.serviceIds ?? []).length) {
            dispatch({ type: "setServices", ids: trimmed });
          }
        } catch (e) {
          // ignore if registry is not ready
        }
      }
      dispatch({ type: "setSpecialist", id });
    },
    toggleService: (id) => {
      console.log("[BookingDraft] action: toggleService", id);
      dispatch({ type: "toggleService", id });
    },
    setServices: (ids) => {
      console.log("[BookingDraft] action: setServices", ids);
      dispatch({ type: "setServices", ids });
    },
    setDateISO: (d) => {
      console.log("[BookingDraft] action: setDateISO", d);
      dispatch({ type: "setDateISO", d });
    },
    setStartISO: (iso) => {
      console.log("[BookingDraft] action: setStartISO", iso);
      dispatch({ type: "setStartISO", iso });
    },
    setLastHour: (offer) => {
      console.log("[BookingDraft] action: setLastHour", offer);
      dispatch({ type: "setLastHour", offer });
    },
    setClaimToken: (token) => {
      console.log("[BookingDraft] action: setClaimToken", token);
      dispatch({ type: "setClaimToken", token });
    },

    // NEW contact actions
    setClientName: (v) => {
      console.log("[BookingDraft] action: setClientName", v);
      dispatch({ type: "setClientName", v });
    },
    setClientPhone: (v) => {
      console.log("[BookingDraft] action: setClientPhone", v);
      dispatch({ type: "setClientPhone", v });
    },
    setClientEmail: (v) => {
      console.log("[BookingDraft] action: setClientEmail", v);
      dispatch({ type: "setClientEmail", v });
    },
    setClientComment: (v) => {
      console.log("[BookingDraft] action: setClientComment", v);
      dispatch({ type: "setClientComment", v });
    },
    setMessengerChannel: (ch) => {
      dispatch({ type: "setMessengerChannel", ch });
    },
    presetConfirm: (m, ch) => {
      dispatch({ type: "presetConfirm", m, ch });
    },
    setSmsConsent: (on) => {
      dispatch({ type: "setSmsConsent", on });
    },
    setConfirmMethod: (m) => {
      console.log("[BookingDraft] action: setConfirmMethod", m);
      dispatch({ type: "setConfirmMethod", m });
    },

    reset: () => {
      console.log("[BookingDraft] action: reset");
      dispatch({ type: "reset" });
    },
  };

  React.useEffect(() => {
    console.log("[BookingDraft] updated", draft);
  }, [draft]);

  return <Ctx.Provider value={{ draft, actions }}>{children}</Ctx.Provider>;
}

export function useBookingDraft() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useBookingDraft must be used within BookingDraftProvider");
  return ctx;
}
